import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import { events } from '@/mocks/events';
import { materialMeta } from '@/utils/content';
import EventCard from '@/components/feature/EventCard';

const statuses = ['全部状态', '持续更新', '辟谣中', '已辟谣'] as const;
const PAGE_SIZE = 9;

export default function Archive() {
  const [sp] = useSearchParams();
  const initialQ = sp.get('q') ?? '';
  const initialType = sp.get('type') ?? 'all';
  const isDebunk = sp.get('view') === 'debunk';

  const [kw, setKw] = useState(initialQ);
  const [typeSel, setTypeSel] = useState<string>(initialType);
  const [statusSel, setStatusSel] = useState<string>(isDebunk ? '辟谣中' : '全部状态');
  const [sort, setSort] = useState<'time' | 'heat'>('time');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const term = kw.trim().toLowerCase();
    let list = events.filter((e) => {
      if (statusSel !== '全部状态') {
        if (statusSel === '辟谣中') {
          if (e.status !== '辟谣中' && e.status !== '已辟谣') return false;
        } else if (e.status !== statusSel) {
          return false;
        }
      }
      if (typeSel !== 'all') {
        const inDominant = e.dominantType === typeSel;
        const inAny = e.materials.some((m) => m.type === typeSel);
        if (!inDominant && !inAny) return false;
      }
      if (term) {
        const hay = `${e.title} ${e.tagline} ${e.summary} ${e.category} ${e.persons.join(' ')} ${e.materials
          .map((m) => m.title)
          .join(' ')}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) =>
      sort === 'heat' ? b.heat - a.heat : (a.time < b.time ? 1 : -1),
    );
    return list;
  }, [kw, typeSel, statusSel, sort]);

  const totalPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPage);
  const pageList = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilter = () => {
    setKw('');
    setTypeSel('all');
    setStatusSel('全部状态');
    setSort('time');
    setPage(1);
  };

  const badge = (label: string, active: boolean, onClick: () => void) => (
    <button
      key={label}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-full whitespace-nowrap transition-colors cursor-pointer ${
        active
          ? 'bg-primary-500 text-background-50'
          : 'bg-background-50 text-foreground-600 border border-background-200 hover:border-primary-300'
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
      <Seo
        title="实锤证据库 - 69爆料｜事件、人物、时间多维检索"
        description="69爆料实锤证据库：支持按事件、人物、时间与素材类型（聊天截图、转账记录、录音影像）组合检索已整理的事件档案，可筛选持续更新、辟谣中、已辟谣状态。"
        keywords="69爆料证据库,69实锤截图检索,事件证据,转账记录,录音文件,辟谣反锤"
        canonicalPath="/archive"
      />
      {/* 页面标题带 */}
      <section className="bg-foreground-950 text-background-50">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <nav className="text-xs text-background-300 flex items-center gap-1.5" aria-label="面包屑">
            <Link to="/" className="hover:text-secondary-300 transition-colors">档案首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>实锤证据库</span>
          </nav>
          <div className="mt-4 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div>
              <h1 className="font-display font-black text-2xl md:text-4xl">实锤证据库</h1>
              <p className="mt-2 text-background-300 text-sm md:text-base max-w-2xl">
                共收录 {events.length} 个事件档案。输入关键词、选择素材类型与事件状态，即可在正反材料与补档时间线之间交叉检索。
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs bg-background-50/10 px-3 py-1.5 rounded-full whitespace-nowrap">
                <i className="ri-database-2-line text-secondary-300"></i>
                {filtered.length} 条结果
              </span>
            </div>
          </div>

          {/* 搜索 + 排序 */}
          <div className="mt-6 flex flex-col lg:flex-row gap-3">
            <div className="flex items-center gap-2 flex-1 bg-background-50/10 border border-background-50/20 rounded-full pl-4 pr-1 py-1 focus-within:border-secondary-300">
              <i className="ri-search-line text-background-300"></i>
              <input
                value={kw}
                onChange={(e) => {
                  setKw(e.target.value);
                  setPage(1);
                }}
                className="flex-1 bg-transparent text-sm text-background-50 outline-none placeholder:text-background-400"
                placeholder="按事件关键词 / 人物 / 素材名检索…"
                aria-label="检索证据库关键词"
              />
              {kw && (
                <button onClick={() => { setKw(''); setPage(1); }} aria-label="清空" className="text-background-300 hover:text-background-50 px-2">
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>
            <div className="inline-flex items-center gap-1 rounded-full bg-background-50/10 p-1 text-xs self-start lg:self-center">
              {(['time', 'heat'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => { setSort(s); setPage(1); }}
                  className={`px-4 py-1.5 rounded-full transition-colors whitespace-nowrap ${
                    sort === s ? 'bg-background-50 text-foreground-950 font-semibold' : 'text-background-200'
                  }`}
                >
                  {s === 'time' ? '按时间' : '按热度'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 筛选项 */}
      <section className="border-b border-background-200/70 bg-background-100/60">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10 py-5 flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500 w-16 shrink-0">素材类型</span>
            {badge('全部', typeSel === 'all', () => { setTypeSel('all'); setPage(1); })}
            {Object.entries(materialMeta).map(([k, v]) =>
              badge(
                v.label,
                typeSel === k,
                () => { setTypeSel(k); setPage(1); },
              ),
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500 w-16 shrink-0">事件状态</span>
            {statuses.map((s) =>
              badge(
                s,
                statusSel === s,
                () => { setStatusSel(s); setPage(1); },
              ),
            )}
          </div>
        </div>
      </section>

      {/* 结果区 */}
      <section className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-12">
        {pageList.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {pageList.map((e) => (
                <EventCard key={e.slug} ev={e} />
              ))}
            </div>
            {totalPage > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-background-200 hover:border-primary-300 text-foreground-700 disabled:opacity-40 cursor-pointer"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                {Array.from({ length: totalPage }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 rounded-lg text-sm transition-colors ${
                      safePage === i + 1
                        ? 'bg-primary-500 text-background-50 font-semibold'
                        : 'border border-background-200 text-foreground-700 hover:border-primary-300'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
                  disabled={safePage === totalPage}
                  className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-background-200 hover:border-primary-300 text-foreground-700 disabled:opacity-40 cursor-pointer"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="mx-auto w-16 h-16 inline-flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl">
              <i className="ri-inbox-line"></i>
            </div>
            <h2 className="mt-4 font-display font-bold text-foreground-950 text-xl">没有找到匹配的档案</h2>
            <p className="mt-2 text-sm text-foreground-500 max-w-md mx-auto">
              换一个关键词，或清空素材类型 / 事件状态筛选后再试。也可通过「我要投稿」补充你掌握的线索。
            </p>
            <button
              onClick={resetFilter}
              className="mt-5 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm rounded-full px-6 py-2.5 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>重置全部筛选
            </button>
          </div>
        )}
      </section>
    </>
  );
}